import type { ComponentChildren, JSX } from "preact";
import { Fragment } from "preact";
import { ButtonGroup } from "@gss/components/ButtonGroup";
import type { Filter } from "@gss/types/measurementStat";
import { ButtonHelp } from "../util/ButtonHelp";
import { useTranslation } from "../util/Translation";
import { ButtonClear } from "./ButtonClear";
import { ButtonMultiSelect } from "./ButtonMultiSelect";
import { isAllOption } from "./FilterUtil";
import { MultiSelectionDesc } from "./MultiSelectionDesc";

interface Props {
    filter: Filter;
    isPublic: boolean;
    inMultiSelection: boolean;
    disabled: boolean;
    clickMultiSelection: () => void;
    clear: () => void;
}

export function FilterButtons({
    filter,
    isPublic,
    inMultiSelection,
    disabled,
    clickMultiSelection,
    clear,
}: Props): JSX.Element {
    const { translateFilter } = useTranslation();
    const enableMultiSelection = !isPublic && allowMultiSelection(filter);

    const renderHelp = () => {
        let description: ComponentChildren = filter.description;

        if (typeof description === "string") {
            const translatedDescription = translateFilter(description);
            const lines = translatedDescription.split("\n");
            if (lines.length > 1) {
                description = lines.map((line, i) => (
                    <Fragment key={i}>
                        {i > 0 && <br />}
                        {line}
                    </Fragment>
                ));
            } else {
                description = translatedDescription;
            }
        }

        const components: ComponentChildren[] = [];

        if (description != null) {
            components.push(<div>{description}</div>);
        }

        if (enableMultiSelection) {
            components.push(
                <div className={components.length > 0 ? "mt-2" : undefined}>
                    <MultiSelectionDesc />
                </div>,
            );
        }

        return components;
    };

    return (
        <ButtonGroup className="filter-buttons float-end">
            {enableMultiSelection && (
                <ButtonMultiSelect
                    disabled={disabled}
                    inMultiSelection={inMultiSelection}
                    onClick={clickMultiSelection}
                />
            )}

            {(filter.description != null || enableMultiSelection) && (
                <ButtonHelp disabled={disabled} popover={renderHelp} />
            )}

            <ButtonClear disabled={disabled} onClick={clear} />
        </ButtonGroup>
    );
}

// Requires 3 or more selectable options.
function allowMultiSelection(filter: Filter): boolean {
    if (filter.options == null) {
        return false;
    }
    // Exclude the default "All" option.
    if (isAllOption(filter.options[0].value)) {
        return filter.options.length > 3;
    }
    return filter.options.length > 2;
}
